import { getApiErrorMessage, IApiAxiosError } from "@/api";
import {
    IClauseSectionTypesUpdate,
    updateClauseSectionTypes,
} from "@/api/clause-section-types";
import {
    getDocumentSectionTypes,
    IDocumentSectionType,
} from "@/api/document-section-types";
import { Modal } from "@/components/shared/Modal";
import {
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { useEffect, useState } from "react";

interface ManageSectionTypesDialogPayload {
    clauseId: number;
    currentSectionTypes: IDocumentSectionType[];
}

export const ManageSectionTypesDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<ManageSectionTypesDialogPayload>) => {
    const { clauseId, currentSectionTypes } = payload;
    const queryClient = useQueryClient();

    const [selectedIds, setSelectedIds] = useState<Set<number>>(
        new Set(currentSectionTypes.map((st) => st.id))
    );

    // Reset selected IDs when dialog opens with new data
    useEffect(() => {
        setSelectedIds(new Set(currentSectionTypes.map((st) => st.id)));
    }, [currentSectionTypes]);

    // Fetch all available section types
    const {
        data: allSectionTypes,
        isLoading,
        isError: isQueryError,
    } = useQuery({
        queryKey: ["document-section-types"],
        queryFn: () => getDocumentSectionTypes(1, 100),
        enabled: open,
    });

    const updateMutation = useMutation({
        mutationFn: (data: IClauseSectionTypesUpdate) =>
            updateClauseSectionTypes(clauseId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["clause-section-types", clauseId],
            });
            handleClose();
        },
    });

    const handleToggle = (sectionTypeId: number) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(sectionTypeId)) {
                newSet.delete(sectionTypeId);
            } else {
                newSet.add(sectionTypeId);
            }
            return newSet;
        });
    };

    const handleSave = () => {
        if (updateMutation.isPending) return;

        updateMutation.mutate({
            document_section_type_ids: Array.from(selectedIds),
        });
    };

    const handleClose = () => {
        onClose();
        setSelectedIds(new Set(currentSectionTypes.map((st) => st.id)));
        updateMutation.reset();
    };

    const axiosError = updateMutation.error as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Manage Section Types"
            subtitle="Select which document section types apply to this clause"
            isLoading={updateMutation.isPending}
            actions={[
                {
                    label: "Save",
                    onClick: handleSave,
                    variant: "contained",
                    color: "primary",
                    disabled: updateMutation.isPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {getApiErrorMessage(
                            axiosError,
                            "An error occurred while updating section types."
                        )}
                    </Typography>
                )
            }
        >
            {isLoading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size={24} />
                </Stack>
            ) : isQueryError ? (
                <Typography variant="body2" color="error" py={2}>
                    Failed to load section types. Please try again.
                </Typography>
            ) : (
                <FormGroup>
                    {allSectionTypes?.items.map((sectionType) => (
                        <FormControlLabel
                            key={sectionType.id}
                            control={
                                <Checkbox
                                    checked={selectedIds.has(sectionType.id)}
                                    onChange={() =>
                                        handleToggle(sectionType.id)
                                    }
                                    disabled={updateMutation.isPending}
                                />
                            }
                            label={
                                <Stack>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                    >
                                        {sectionType.name}
                                    </Typography>
                                    {sectionType.description && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {sectionType.description}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            sx={{ mb: 1, alignItems: "flex-start" }}
                        />
                    ))}
                </FormGroup>
            )}
        </Modal>
    );
};
